<script setup>
import { nextTick, ref, watch } from 'vue'
import { Badge } from '@drama/ui'
import { GitBranch } from '@lucide/vue'
import { useI18n } from 'vue-i18n'
import ConversationCallTimeline from './ConversationCallTimeline.vue'
import IntentVideoTaskSummary from './IntentVideoTaskSummary.vue'
import LongText from '../../../components/LongText.vue'

const props = defineProps({
  round: { type: Object, required: true },
  runId: { type: String, required: true },
  conversationKey: { type: String, required: true },
  targeted: { type: Boolean, required: true },
})

const { t } = useI18n()
const wrapper = ref(null)

watch(() => props.targeted, async (targeted) => {
  if (!targeted) return
  await nextTick()
  wrapper.value.scrollIntoView({ block: 'center' })
}, { immediate: true })

function modeLabel() {
  switch (props.round.mode) {
    case 'act':
    case 'say':
      return t(`visualGameAdmin.llm.conversations.detail.modes.${props.round.mode}`)
    default:
      return props.round.mode
  }
}

function matchVariant() {
  switch (props.round.video_match.status) {
    case 'matched':
      return 'success'
    case 'unmatched':
      return 'neutral'
    case 'not_evaluated':
      return 'warning'
    default:
      return 'neutral'
  }
}

function matchReason() {
  switch (props.round.video_match.status) {
    case 'matched':
      return props.round.video_match.reason === null
        ? t('visualGameAdmin.llm.conversations.detail.videoMatch.noReason')
        : props.round.video_match.reason
    case 'unmatched':
      return props.round.video_match.reason
    case 'not_evaluated':
      return t('visualGameAdmin.llm.conversations.detail.videoMatch.noReason')
    default:
      return props.round.video_match.reason ?? props.round.video_match.status
  }
}
</script>

<template>
  <li>
    <details ref="wrapper" class="conversation-round" :class="{ 'is-targeted': targeted }" open>
      <summary class="conversation-round__header">
        <span class="conversation-round__title">
          <strong>{{ t('visualGameAdmin.llm.conversations.detail.round', { number: round.turn_no }) }}</strong>
          <Badge variant="neutral" size="sm">{{ modeLabel() }}</Badge>
        </span>
        <span>{{ t('visualGameAdmin.llm.conversations.detail.callsInRound', { count: round.calls.length }) }}</span>
      </summary>

      <div class="conversation-round__body">
        <section class="conversation-round__input">
          <LongText
            :label="t('visualGameAdmin.llm.conversations.detail.playerInput')"
            :text="round.input"
            :copy-text="round.input"
            :collapse-over="300"
            markdown
          />
        </section>

        <ConversationCallTimeline :calls="round.calls" />

        <section class="conversation-round__video-match">
          <div class="conversation-round__video-match-summary">
            <span>
              <strong>{{ t('visualGameAdmin.llm.conversations.detail.videoMatch.title') }}</strong>
              <Badge :variant="matchVariant()" size="sm">
                {{ t(`visualGameAdmin.llm.conversations.detail.videoMatch.status.${round.video_match.status}`) }}
              </Badge>
            </span>
            <small>{{ matchReason() }}</small>
          </div>
          <RouterLink
            class="conversation-round__video-match-link"
            :to="{
              name: 'llm-video-match-detail',
              params: { runId, conversationKey, turnId: round.turn_id },
            }"
            data-testid="video-match-detail-link"
          >
            <GitBranch :size="16" />
            {{ t('visualGameAdmin.llm.conversations.detail.videoMatch.open') }}
          </RouterLink>
        </section>

        <IntentVideoTaskSummary v-if="round.intent_video_task !== null" :task="round.intent_video_task" />
      </div>
    </details>
  </li>
</template>

<style scoped>
.conversation-round {
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-lg);
  background: var(--drama-color-surface);
}

.conversation-round__body {
  display: grid;
  gap: var(--drama-space-lg);
  margin-top: var(--drama-space-lg);
}

.conversation-round__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
  cursor: pointer;
}

.conversation-round__title {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
}

.conversation-round.is-targeted {
  border-color: var(--drama-color-primary);
  box-shadow: var(--drama-shadow-md);
}

.conversation-round__header span {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.conversation-round__input {
  width: min(72%, 680px);
  margin-left: auto;
}

.conversation-round__input :deep(.long-text__body) {
  padding: var(--drama-space-sm) var(--drama-space-md);
  border-color: color-mix(in srgb, var(--drama-color-info) 45%, var(--drama-color-border));
  background: color-mix(in srgb, var(--drama-color-info) 9%, var(--drama-color-surface));
}

.conversation-round__video-match {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
  padding-top: var(--drama-space-md);
  border-top: 1px solid var(--drama-color-border);
}

.conversation-round__video-match-summary {
  display: grid;
  gap: var(--drama-space-xs);
  min-width: 0;
}

.conversation-round__video-match-summary > span {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
}

.conversation-round__video-match-summary small {
  color: var(--drama-color-text-muted);
  overflow-wrap: anywhere;
}

.conversation-round__video-match-link {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: var(--drama-space-xs);
  min-height: var(--drama-control-height-sm);
  padding: var(--drama-space-xs) var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-sm);
  font-weight: var(--drama-font-weight-medium);
  text-decoration: none;
}

.conversation-round__video-match-link:hover {
  border-color: var(--drama-color-primary);
  color: var(--drama-color-primary);
}

@media (max-width: 680px) {
  .conversation-round__input {
    width: 100%;
  }

  .conversation-round__video-match {
    align-items: stretch;
    flex-direction: column;
  }
}

</style>
